import React, { useEffect, useState } from 'react';
import useDownloadWithPassword from '../../hooks/useDownloadWithPassword';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  PlayArrow as TestIcon,
  Download as ExportIcon,
  Upload as ImportIcon
} from '@mui/icons-material';
import { toastMessage } from '../../utils/toastMessage';
import useFullPageLoader from '../../hooks/useFullPageLoader';
import useButtonLoader from '../../hooks/UseButtonLoader';
import {
  getNotificationMessagesApi,
  createNotificationMessageApi,
  updateNotificationMessageApi,
  deleteNotificationMessageApi,
  bulkOperationApi,
  getCategoriesApi,
  getLanguagesApi,
  getTargetAudiencesApi,
  testNotificationApi,
  exportMessagesApi,
  importMessagesApi,
  handleApiResponse,
  handleApiError,
  formatMessageData,
  validateMessageData
} from '../../services/notificationManagement/enhancedNotificationService';

const EnhancedNotificationManagement = () => {
  // State management
  const [activeTab, setActiveTab] = useState(0);
  const [messages, setMessages] = useState([]);
  const [totalMessages, setTotalMessages] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    category: '',
    language: '',
    isActive: '',
    search: '',
    priority: '',
    targetAudience: '',
    screen: '',
    timeRange: '',
    startDate: '',
    endDate: '',
    usageCount: ''
  });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Modal states
  const [messageModal, setMessageModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [selectedMessages, setSelectedMessages] = useState([]);
  
  // Form states
  const [messageForm, setMessageForm] = useState({
    title: '',
    body: '',
    category: '',
    timing: '',
    priority: 5,
    language: 'hi',
    targetAudience: 'all',
    screen: 'Home',
    tags: '',
    isActive: true
  });
  
  // Options states
  const [categories, setCategories] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [targetAudiences, setTargetAudiences] = useState([]);
  
  // Hooks
  const [loader, showLoader, hideLoader] = useFullPageLoader();
  const [buttonRef, buttonLoader] = useButtonLoader('Save');
  const { requestDownloadWithCallback, DownloadPasswordModal } = useDownloadWithPassword();
  
  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);
  
  // Load messages when filters or pagination changes
  useEffect(() => {
    loadMessages();
  }, [currentPage, pageSize, filters, sortBy, sortOrder]);
  
  const loadInitialData = async () => {
    try {
      showLoader();
      const [categoriesData, languagesData, audiencesData] = await Promise.all([
        getCategoriesApi(),
        getLanguagesApi(),
        getTargetAudiencesApi()
      ]);
      
      setCategories(categoriesData.data || []);
      setLanguages(languagesData.data || []);
      setTargetAudiences(audiencesData.data || []);
    } catch (error) {
      handleApiError(error);
    } finally {
      hideLoader();
    }
  };
  
  const loadMessages = async () => {
    try {
      setLoading(true);
      
      // Build filter parameters
      const params = {
        page: currentPage,
        limit: pageSize,
        sortBy,
        sortOrder
      };

      // Add filters only if they have values
      if (filters.search) params.search = filters.search;
      if (filters.category) params.category = filters.category;
      if (filters.language) params.language = filters.language;
      if (filters.isActive !== '') params.isActive = filters.isActive;
      if (filters.priority) params.priority = filters.priority;
      if (filters.targetAudience) params.targetAudience = filters.targetAudience;
      if (filters.screen) params.screen = filters.screen;
      if (filters.usageCount) params.usageCount = filters.usageCount;

      // Handle time range filters
      if (filters.timeRange && filters.timeRange !== 'custom') {
        params.timeRange = filters.timeRange;
      } else if (filters.timeRange === 'custom' && filters.startDate && filters.endDate) {
        params.startDate = filters.startDate;
        params.endDate = filters.endDate;
      }
      
      const response = await getNotificationMessagesApi(params);
      let result = response?.data;
      console.log("response", result?.data);
      setMessages(result?.data.messages || []);
      setTotalMessages(result.data.pagination?.totalItems || 0);
    } catch (error) {
      handleApiError(error);
    } finally {
      setLoading(false);
    }
  };
  
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };
  
  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Don't reset page immediately, let user apply filters manually
  };

  const applyFilters = () => {
    setCurrentPage(1);
    loadMessages();
  };

  const clearFilters = () => {
    setFilters({
      category: '',
      language: '',
      isActive: '',
      search: '',
      priority: '',
      targetAudience: '',
      screen: '',
      timeRange: '',
      startDate: '',
      endDate: '',
      usageCount: ''
    });
    setCurrentPage(1);
    loadMessages();
  };
  
  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };
  
  const openMessageModal = (message = null) => {
    if (message) {
      setSelectedMessage(message);
      setMessageForm({
        title: message.title,
        body: message.body,
        category: message.category,
        timing: message.timing || '',
        priority: message.priority || 5,
        language: message.language || 'hi',
        targetAudience: message.targetAudience || 'all',
        screen: message.screen || 'Home',
        tags: Array.isArray(message.tags) ? message.tags.join(', ') : '',
        isActive: message.isActive
      });
    } else {
      setSelectedMessage(null);
      setMessageForm({
        title: '',
        body: '',
        category: '',
        timing: '',
        priority: 5,
        language: 'hi',
        targetAudience: 'all',
        screen: 'Home',
        tags: '',
        isActive: true
      });
    }
    setMessageModal(true);
  };
  
  const handleFormChange = (field, value) => {
    setMessageForm(prev => ({ ...prev, [field]: value }));
  };
  
  const handleSubmit = async () => {
    try {
      const errors = validateMessageData(messageForm);
      if (errors.length > 0) {
        toastMessage('error', errors.join(', '));
        return;
      }
      
      buttonLoader(true);
      const formattedData = formatMessageData(messageForm);
      
      if (selectedMessage) {
        await updateNotificationMessageApi(selectedMessage._id, formattedData);
        toastMessage('success', 'Message updated successfully');
      } else {
        await createNotificationMessageApi(formattedData);
        toastMessage('success', 'Message created successfully');
      }
      
      setMessageModal(false);
      loadMessages();
    } catch (error) {
      handleApiError(error);
    } finally {
      buttonLoader(false);
    }
  };
  
  const handleDelete = async () => {
    try {
      if (selectedMessages.length > 0) {
        await bulkOperationApi('delete', selectedMessages);
        toastMessage('success', `${selectedMessages.length} messages deleted successfully`);
        setSelectedMessages([]);
      } else if (selectedMessage) {
        await deleteNotificationMessageApi(selectedMessage._id);
        toastMessage('success', 'Message deleted successfully');
      }
      
      setDeleteModal(false);
      loadMessages();
    } catch (error) {
      handleApiError(error);
    }
  };
  
  const handleBulkOperation = async (operation) => {
    try {
      if (selectedMessages.length === 0) {
        toastMessage('error', 'Please select messages first');
        return;
      }
      
      await bulkOperationApi(operation, selectedMessages);
      toastMessage('success', `Bulk ${operation} completed successfully`);
      setSelectedMessages([]);
      loadMessages();
    } catch (error) {
      handleApiError(error);
    }
  };
  
  const handleTestMessage = async (messageId) => {
    try {
      await testNotificationApi(messageId);
      toastMessage('success', 'Test notification sent successfully');
    } catch (error) {
      handleApiError(error);
    }
  };
  
  const handleExport = (format = 'json') => {
    const params = { format, ...filters };
    requestDownloadWithCallback(async () => {
      try {
        const response = await exportMessagesApi(params);
        if (format === 'csv') {
          const blob = new Blob([response], { type: 'text/csv' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `notification-messages-${new Date().toISOString().split('T')[0]}.csv`;
          a.click();
          window.URL.revokeObjectURL(url);
        } else {
          const blob = new Blob([JSON.stringify(response, null, 2)], { type: 'application/json' });
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `notification-messages-${new Date().toISOString().split('T')[0]}.json`;
          a.click();
          window.URL.revokeObjectURL(url);
        }
        toastMessage('success', 'Export completed successfully');
      } catch (error) {
        handleApiError(error);
      }
    }, 'Notification Management');
  };
  
  const handleSelectMessage = (messageId) => {
    setSelectedMessages(prev => {
      if (prev.includes(messageId)) {
        return prev.filter(id => id !== messageId);
      } else {
        return [...prev, messageId];
      }
    });
  };
  
  const handleSelectAll = () => {
    if (selectedMessages.length === messages.length) {
      setSelectedMessages([]);
    } else {
      setSelectedMessages(messages.map(msg => msg._id));
    }
  };
  
  const getCategoryColor = (category) => {
    const colors = {
      morning: 'success',
      afternoon: 'warning',
      evening: 'info',
      night: 'secondary',
      breakfast: 'primary',
      lunch: 'success',
      dinner: 'warning',
      sunrise: 'success',
      sunset: 'warning',
      weekly_monday: 'primary',
      weekly_weekend: 'secondary',
      monthly_first: 'success',
      monthly_mid: 'info',
      monthly_last: 'warning',
      astrological_weekly: 'primary',
      astrological_monthly: 'secondary',
      custom: 'default'
    };
    return colors[category] || 'default';
  };
  
  const getLanguageName = (code) => {
    const language = languages.find(lang => lang.code === code);
    return language ? language.name : code;
  };
  
  const getTargetAudienceName = (audience) => {
    const audienceMap = {
      all: 'All Users',
      new_users: 'New Users',
      active_users: 'Active Users',
      premium_users: 'Premium Users',
      consultants: 'Consultants'
    };
    return audienceMap[audience] || audience;
  };
  
  return (
    <>
      
      
         
                    {/* Messages Tab */}
          <>
            {/* Action Buttons */}
            <div className="block_user_button mb-2">
              <button 
                onClick={() => openMessageModal()}
                style={{
                  backgroundColor: '#ff8989',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginRight: '10px'
                }}
              >
                <AddIcon />
                Add Message
              </button>
             
            </div>
            
            {/* Advanced Filters */}
            <div className="FilterBox FilterArea" style={{ 
              flexDirection: 'column', 
              marginBottom: '20px',
              backgroundColor: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e9ecef',
              padding: '20px'
            }}>
              {/* Row 1: Search Bar and Basic Filters */}
              <div className="group_filter_value" style={{ 
                marginBottom: '15px', 
                display: 'flex !important', 
                flexWrap: 'wrap !important', 
                gap: '15px', 
                alignItems: 'center',
                width: '100%',
                flexDirection: 'row'
              }}>
                {/* Search Bar */}
                <div className="form-group search_md_size userSearch" style={{ minWidth: '200px', flex: '1 1 auto' }}>
                  <input
                    type="search"
                    className={`form-control ${filters.search && "active"}`}
                    placeholder="Search messages..."
                    value={filters.search || ""}
                    style={{ "--color": "#FF8989" }}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                  />
                </div>

                {/* Category Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.category && "active"}`}
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                  >
                    <option value="">All Categories</option>
                    <optgroup label="Daily Notifications">
                      <option value="morning">🌅 Morning</option>
                      <option value="afternoon">☀️ Afternoon</option>
                      <option value="evening">🌆 Evening</option>
                      <option value="night">🌙 Night</option>
                    </optgroup>
                    <optgroup label="Meal Time Notifications">
                      <option value="breakfast">🍳 Breakfast</option>
                      <option value="lunch">🍽️ Lunch</option>
                      <option value="dinner">🍴 Dinner</option>
                    </optgroup>
                    <optgroup label="Astrological Timing">
                      <option value="sunrise">🌅 Sunrise</option>
                      <option value="sunset">🌇 Sunset</option>
                    </optgroup>
                    <optgroup label="Weekly Notifications">
                      <option value="weekly_monday">📅 Monday</option>
                      <option value="weekly_weekend">🎉 Weekend</option>
                    </optgroup>
                    <optgroup label="Monthly Notifications">
                      <option value="monthly_first">🌟 First Day</option>
                      <option value="monthly_mid">📊 Mid Month</option>
                      <option value="monthly_last">🏁 Last Day</option>
                    </optgroup>
                    <optgroup label="Astrological Cycles">
                      <option value="astrological_weekly">🔮 Weekly Astro</option>
                      <option value="astrological_monthly">🌙 Monthly Astro</option>
                    </optgroup>
                    <optgroup label="Custom">
                      <option value="custom">⚙️ Custom</option>
                    </optgroup>
                  </select>
                </div>

                {/* Language Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.language && "active"}`}
                    value={filters.language}
                    onChange={(e) => handleFilterChange('language', e.target.value)}
                  >
                    <option value="">All Languages</option>
                    {languages && languages?.length > 0 && languages?.map(lang => (
                      <option key={lang.code} value={lang.code}>
                        {lang.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.isActive && "active"}`}
                    value={filters.isActive}
                    onChange={(e) => handleFilterChange('isActive', e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="true">Active</option>
                    <option value="false">Inactive</option>
                  </select>
                </div>

                {/* Priority Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.priority && "active"}`}
                    value={filters.priority}
                    onChange={(e) => handleFilterChange('priority', e.target.value)}
                  >
                    <option value="">All Priorities</option>
                    <option value="1-3">Low (1-3)</option>
                    <option value="4-6">Medium (4-6)</option>
                    <option value="7-10">High (7-10)</option>
                  </select>
                </div>
              </div>

              {/* Row 2: Additional Filters and Time Range */}
              <div className="group_filter_value" style={{ 
                display: 'flex !important', 
                flexWrap: 'wrap !important', 
                gap: '15px', 
                alignItems: 'center',
                width: '100%',
                flexDirection: 'row'
              }}>
                {/* Target Audience */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.targetAudience && "active"}`}
                    value={filters.targetAudience}
                    onChange={(e) => handleFilterChange('targetAudience', e.target.value)}
                  >
                    <option value="">All Audiences</option>
                    {targetAudiences && targetAudiences?.length > 0 && targetAudiences?.map(audience => (
                      <option key={audience} value={audience}>
                        {getTargetAudienceName(audience)}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Screen Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.screen && "active"}`}
                    value={filters.screen}
                    onChange={(e) => handleFilterChange('screen', e.target.value)}
                  >
                    <option value="">All Screens</option>
                    <option value="Home">Home</option>
                    <option value="DailyHoroscope">Daily Horoscope</option>
                    <option value="WeeklyHoroscope">Weekly Horoscope</option>
                    <option value="Profile">Profile</option>
                    <option value="Settings">Settings</option>
                  </select>
                </div>

                {/* Time Range Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.timeRange && "active"}`}
                    value={filters.timeRange}
                    onChange={(e) => handleFilterChange('timeRange', e.target.value)}
                  >
                    <option value="">All Time Ranges</option>
                    <option value="today">Today</option>
                    <option value="yesterday">Yesterday</option>
                    <option value="last7days">Last 7 Days</option>
                    <option value="last30days">Last 30 Days</option>
                    <option value="last90days">Last 90 Days</option>
                    <option value="thisMonth">This Month</option>
                    <option value="lastMonth">Last Month</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>
                
                {filters.timeRange === 'custom' && (
                  <>
                    <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.startDate || ''}
                        onChange={(e) => handleFilterChange('startDate', e.target.value)}
                        placeholder="Start Date"
                      />
                    </div>
                    <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                      <input
                        type="date"
                        className="form-control"
                        value={filters.endDate || ''}
                        onChange={(e) => handleFilterChange('endDate', e.target.value)}
                        placeholder="End Date"
                      />
                    </div>
                  </>
                )}

                {/* Usage Count Filter */}
                <div className="form-group select_sm_width" style={{ minWidth: '150px', flex: '0 0 auto' }}>
                  <select
                    className={`form-control ${filters.usageCount && "active"}`}
                    value={filters.usageCount}
                    onChange={(e) => handleFilterChange('usageCount', e.target.value)}
                  >
                    <option value="">All Usage Counts</option>
                    <option value="0">Never Used (0)</option>
                    <option value="1-10">Low Usage (1-10)</option>
                    <option value="11-50">Medium Usage (11-50)</option>
                    <option value="51+">High Usage (51+)</option>
                  </select>
                </div>

                {/* Apply Filter & Refresh */}
                <div className="form-group refresh_button" style={{ minWidth: '120px', flex: '0 0 auto', display: 'flex', gap: '10px' }}>
                  <button
                    onClick={applyFilters}
                    style={{
                      backgroundColor: '#ff8989',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Apply
                  </button>
                  <button
                    onClick={clearFilters}
                    style={{
                      backgroundColor: '#6c757d',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '5px',
                      cursor: 'pointer',
                      fontSize: '14px'
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>

              {/* Category Quick Filters */}
            
            </div>
              
            

            {/* Bulk Actions */}
            {selectedMessages.length > 0 && (
                <div style={{ 
                  backgroundColor: '#ff8989', 
                  color: 'white', 
                  padding: '15px 20px', 
                  borderRadius: '8px', 
                  marginBottom: '20px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ fontWeight: '500' }}>
                    {selectedMessages.length} message(s) selected
                  </span>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => handleBulkOperation('activate')}
                      style={{
                        backgroundColor: '#28a745',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Activate
                    </button>
                    <button
                      onClick={() => handleBulkOperation('deactivate')}
                      style={{
                        backgroundColor: '#ffc107',
                        color: '#212529',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Deactivate
                    </button>
                    <button
                      onClick={() => setDeleteModal(true)}
                      style={{
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '5px',
                        cursor: 'pointer',
                        fontSize: '14px'
                      }}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              )}
              
              {/* Messages Table */}
              <div className="content-table">
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: '50px' }}>
                        <input
                          type="checkbox"
                          checked={selectedMessages.length === messages.length && messages.length > 0}
                          onChange={handleSelectAll}
                          style={{ width: '16px', height: '16px' }}
                        />
                      </th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Language</th>
                      <th>Timing</th>
                      <th>Priority</th>
                      <th>Status</th>
                      <th>Usage</th>
                      <th className="text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', padding: '40px' }}>
                          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                            <div style={{ 
                              width: '30px', 
                              height: '30px', 
                              border: '3px solid #f3f3f3', 
                              borderTop: '3px solid #ff8989', 
                              borderRadius: '50%', 
                              animation: 'spin 1s linear infinite' 
                            }}></div>
                            <span style={{ marginLeft: '10px' }}>Loading messages...</span>
                          </div>
                        </td>
                      </tr>
                    ) : messages.length === 0 ? (
                      <tr>
                        <td colSpan={9} style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                          No messages found
                        </td>
                      </tr>
                    ) : (
                      messages && messages?.length > 0 && messages?.map((message) => (
                        <tr key={message._id}>
                          <td>
                            <input
                              type="checkbox"
                              checked={selectedMessages.includes(message._id)}
                              onChange={() => handleSelectMessage(message._id)}
                              style={{ width: '16px', height: '16px' }}
                            />
                          </td>
                          <td>
                            <span style={{ fontWeight: '500' }}>
                              {message.title}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              backgroundColor: getCategoryColor(message.category) === 'success' ? '#d4edda' : 
                                             getCategoryColor(message.category) === 'warning' ? '#fff3cd' : 
                                             getCategoryColor(message.category) === 'info' ? '#d1ecf1' : 
                                             getCategoryColor(message.category) === 'error' ? '#f8d7da' : '#e2e3e5',
                              color: getCategoryColor(message.category) === 'success' ? '#155724' : 
                                     getCategoryColor(message.category) === 'warning' ? '#856404' : 
                                     getCategoryColor(message.category) === 'info' ? '#0c5460' : 
                                     getCategoryColor(message.category) === 'error' ? '#721c24' : '#383d41',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {message.category}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#666' }}>
                              {message.language}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#666' }}>
                              {message.timing || 'N/A'}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              backgroundColor: message.priority >= 8 ? '#f8d7da' : 
                                             message.priority >= 5 ? '#fff3cd' : '#d4edda',
                              color: message.priority >= 8 ? '#721c24' : 
                                     message.priority >= 5 ? '#856404' : '#155724',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {message.priority}
                            </span>
                          </td>
                          <td>
                            <span style={{
                              backgroundColor: message.isActive ? '#d4edda' : '#f8d7da',
                              color: message.isActive ? '#155724' : '#721c24',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {message.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </td>
                          <td>
                            <span style={{ color: '#666', fontWeight: '500' }}>
                              {message.usageCount || 0}
                            </span>
                          </td>
                          <td>
                            <div className="action_button" style={{ display: 'flex', gap: '5px', justifyContent: 'center' }}>
                              <button
                                onClick={() => openMessageModal(message)}
                                style={{
                                  backgroundColor: '#17a2b8',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="View"
                              >
                                <ViewIcon style={{ fontSize: '16px' }} />
                              </button>
                              <button
                                onClick={() => openMessageModal(message)}
                                style={{
                                  backgroundColor: '#ffc107',
                                  color: '#212529',
                                  border: 'none',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Edit"
                              >
                                <EditIcon style={{ fontSize: '16px' }} />
                              </button>
                              <button
                                onClick={() => handleTestMessage(message._id)}
                                style={{
                                  backgroundColor: '#28a745',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Test"
                              >
                                <TestIcon style={{ fontSize: '16px' }} />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedMessage(message);
                                  setDeleteModal(true);
                                }}
                                style={{
                                  backgroundColor: '#dc3545',
                                  color: 'white',
                                  border: 'none',
                                  padding: '6px 10px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '12px'
                                }}
                                title="Delete"
                              >
                                <DeleteIcon style={{ fontSize: '16px' }} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              
              {/* Filter Summary */}
              {Object.values(filters).some(value => value !== '' && value !== null && value !== undefined) && (
                <div style={{ 
                  backgroundColor: '#e3f2fd', 
                  border: '1px solid #2196f3', 
                  borderRadius: '8px', 
                  padding: '15px', 
                  marginTop: '20px',
                  marginBottom: '20px'
                }}>
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '10px'
                  }}>
                    <h4 style={{ margin: 0, color: '#1976d2', fontSize: '16px' }}>
                      Active Filters
                    </h4>
                    <button
                      onClick={clearFilters}
                      style={{
                        backgroundColor: 'transparent',
                        color: '#1976d2',
                        border: '1px solid #1976d2',
                        padding: '5px 10px',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '12px'
                      }}
                    >
                      Clear All
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {Object.entries(filters).map(([key, value]) => {
                      if (value && value !== '' && value !== null && value !== undefined) {
                        return (
                          <span
                            key={key}
                            style={{
                              backgroundColor: '#bbdefb',
                              color: '#1976d2',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                          >
                            {key.charAt(0).toUpperCase() + key.slice(1)}: {value}
                            <button
                              onClick={() => handleFilterChange(key, '')}
                              style={{
                                backgroundColor: 'transparent',
                                border: 'none',
                                color: '#1976d2',
                                cursor: 'pointer',
                                fontSize: '14px',
                                padding: '0',
                                marginLeft: '5px'
                              }}
                            >
                              ×
                            </button>
                          </span>
                        );
                      }
                      return null;
                    })}
                  </div>
                </div>
              )}

              {/* Pagination */}
              {totalMessages > 0 && (
                <div className="Pagination" style={{ marginTop: '20px' }}>
                  {/* Pagination Info */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    marginBottom: '15px',
                    padding: '0 10px'
                  }}>
                    <div style={{ color: '#666', fontSize: '14px' }}>
                      Showing {((currentPage - 1) * pageSize) + 1} to {Math.min(currentPage * pageSize, totalMessages)} of {totalMessages} messages
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ color: '#666', fontSize: '14px' }}>Items per page:</span>
                      <select
                        value={pageSize}
                        onChange={(e) => {
                          setPageSize(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        style={{
                          padding: '5px 10px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          fontSize: '14px'
                        }}
                      >
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                    </div>
                  </div>

                  {/* Pagination Controls */}
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    gap: '5px',
                    alignItems: 'center'
                  }}>
                    <button
                      onClick={() => handlePageChange(null, 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ff8989',
                        backgroundColor: 'transparent',
                        color: '#ff8989',
                        borderRadius: '4px',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                      title="First Page"
                    >
                      «
                    </button>
                    
                    <button
                      onClick={() => handlePageChange(null, currentPage - 1)}
                      disabled={currentPage === 1}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ff8989',
                        backgroundColor: 'transparent',
                        color: '#ff8989',
                        borderRadius: '4px',
                        cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                        opacity: currentPage === 1 ? 0.5 : 1
                      }}
                    >
                      Previous
                    </button>
                    
                    {/* Page Numbers */}
                    {(() => {
                      const totalPages = Math.ceil(totalMessages / pageSize);
                      const maxVisiblePages = 5;
                      let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                      let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                      
                      if (endPage - startPage + 1 < maxVisiblePages) {
                        startPage = Math.max(1, endPage - maxVisiblePages + 1);
                      }
                      
                      const pages = [];
                      for (let i = startPage; i <= endPage; i++) {
                        pages.push(i);
                      }
                      
                      return pages.map(pageNum => (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(null, pageNum)}
                          style={{
                            padding: '8px 12px',
                            border: '1px solid #ff8989',
                            backgroundColor: currentPage === pageNum ? '#ff8989' : 'transparent',
                            color: currentPage === pageNum ? 'white' : '#ff8989',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontWeight: currentPage === pageNum ? 'bold' : 'normal',
                            minWidth: '40px'
                          }}
                        >
                          {pageNum}
                        </button>
                      ));
                    })()}
                    
                    <button
                      onClick={() => handlePageChange(null, currentPage + 1)}
                      disabled={currentPage === Math.ceil(totalMessages / pageSize)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ff8989',
                        backgroundColor: 'transparent',
                        color: '#ff8989',
                        borderRadius: '4px',
                        cursor: currentPage === Math.ceil(totalMessages / pageSize) ? 'not-allowed' : 'pointer',
                        opacity: currentPage === Math.ceil(totalMessages / pageSize) ? 0.5 : 1
                      }}
                    >
                      Next
                    </button>
                    
                    <button
                      onClick={() => handlePageChange(null, Math.ceil(totalMessages / pageSize))}
                      disabled={currentPage === Math.ceil(totalMessages / pageSize)}
                      style={{
                        padding: '8px 12px',
                        border: '1px solid #ff8989',
                        backgroundColor: 'transparent',
                        color: '#ff8989',
                        borderRadius: '4px',
                        cursor: currentPage === Math.ceil(totalMessages / pageSize) ? 'not-allowed' : 'pointer',
                        opacity: currentPage === Math.ceil(totalMessages / pageSize) ? 0.5 : 1
                      }}
                      title="Last Page"
                    >
                      »
                    </button>
                  </div>
                </div>
              )}
            </>
          
          
      
      {/* Message Modal */}
      {messageModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              backgroundColor: '#ff8989',
              color: 'white',
              padding: '20px 24px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                {selectedMessage ? 'Edit Message' : 'Add New Message'}
              </h3>
            </div>
            
            <div style={{ padding: '24px' }}>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', 
                gap: '20px' 
              }}>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Title *
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageForm.title}
                    onChange={(e) => handleFormChange('title', e.target.value)}
                    placeholder="Enter message title"
                    required
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Category *
                  </label>
                  <select
                    className="form-control"
                    value={messageForm.category}
                    onChange={(e) => handleFormChange('category', e.target.value)}
                    required
                    style={{ width: '100%' }}
                  >
                    <option value="">Select Category</option>
                    <option value="morning">Morning</option>
                    <option value="afternoon">Afternoon</option>
                    <option value="evening">Evening</option>
                    <option value="night">Night</option>
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="dinner">Dinner</option>
                    <option value="sunrise">Sunrise</option>
                    <option value="sunset">Sunset</option>
                    <option value="weekly_monday">Weekly Monday</option>
                    <option value="weekly_weekend">Weekly Weekend</option>
                    <option value="monthly_first">Monthly First</option>
                    <option value="monthly_mid">Monthly Mid</option>
                    <option value="monthly_last">Monthly Last</option>
                    <option value="astrological_weekly">Astrological Weekly</option>
                    <option value="astrological_monthly">Astrological Monthly</option>
                    <option value="custom">Custom</option>

                  </select>
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Body *
                  </label>
                  <textarea
                    className="form-control"
                    value={messageForm.body}
                    onChange={(e) => handleFormChange('body', e.target.value)}
                    placeholder="Enter message body"
                    rows={3}
                    required
                    style={{ width: '100%', resize: 'vertical' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Timing (HH:MM)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageForm.timing}
                    onChange={(e) => handleFormChange('timing', e.target.value)}
                    placeholder="07:00"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Priority (1-10)
                  </label>
                  <input
                    type="number"
                    className="form-control"
                    value={messageForm.priority}
                    onChange={(e) => handleFormChange('priority', e.target.value)}
                    min="1"
                    max="10"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Language
                  </label>
                  <select
                    className="form-control"
                    value={messageForm.language}
                    onChange={(e) => handleFormChange('language', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Select Language</option>
                    <option value="en">English</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Target Audience
                  </label>
                  <select
                    className="form-control"
                    value={messageForm.targetAudience}
                    onChange={(e) => handleFormChange('targetAudience', e.target.value)}
                    style={{ width: '100%' }}
                  >
                    <option value="">Select Target Audience</option>
                   <option value="all">All</option>
                   <option value="new_users">New Users</option>
                   <option value="active_users">Active Users</option>
                   <option value="premium_users">Premium Users</option>
                   <option value="consultants">Consultants</option>
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Screen
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageForm.screen}
                    onChange={(e) => handleFormChange('screen', e.target.value)}
                    placeholder="Home"
                    style={{ width: '100%' }}
                  />
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
                    Tags (comma separated)
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    value={messageForm.tags}
                    onChange={(e) => handleFormChange('tags', e.target.value)}
                    placeholder="morning, motivation, daily"
                    style={{ width: '100%' }}
                  />
                </div>
                <div style={{ gridColumn: '1 / -1' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={messageForm.isActive}
                      onChange={(e) => handleFormChange('isActive', e.target.checked)}
                      style={{ width: '16px', height: '16px' }}
                    />
                    <span style={{ fontWeight: '500', color: '#333' }}>Active</span>
                  </label>
                </div>
              </div>
            </div>
            
            <div style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => setMessageModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                ref={buttonRef}
                onClick={handleSubmit}
                style={{
                  backgroundColor: '#ff8989',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                {selectedMessage ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Delete Confirmation Modal */}
      {deleteModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '500px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.2)'
          }}>
            <div style={{
              backgroundColor: '#dc3545',
              color: 'white',
              padding: '20px 24px',
              borderRadius: '12px 12px 0 0',
              borderBottom: '1px solid #e0e0e0'
            }}>
              <h3 style={{ margin: 0, fontSize: '18px' }}>
                Confirm Delete
              </h3>
            </div>
            
            <div style={{ padding: '24px' }}>
              <p style={{ margin: 0, fontSize: '16px', color: '#333' }}>
                Are you sure you want to delete {selectedMessages.length > 0 ? `${selectedMessages.length} messages` : 'this message'}?
                This action cannot be undone.
              </p>
            </div>
            
            <div style={{ 
              padding: '16px 24px', 
              borderTop: '1px solid #e0e0e0',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '10px'
            }}>
              <button
                onClick={() => setDeleteModal(false)}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                style={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  border: 'none',
                  padding: '10px 20px',
                  borderRadius: '5px',
                  cursor: 'pointer'
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <DownloadPasswordModal />
    </>
  );
};

export default EnhancedNotificationManagement;
